/**
 * The pre-redesign review header (main before 2026-10), kept for every review that isn't
 * headphones, earbuds or headsets (`isListeningGear`): hero, product short, features, pros and cons, score rings and the reader-scorable `ReviewScores`. The title
 * block renders inside the hero as `children`.
 */
import clsx from 'clsx'
import { getds } from 'ds'
import React, { useCallback } from 'react'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import styleUtils from 'shared-components/styles/util.module.css'
import BadgedProductShort from 'shared-components/ui/BadgedProductShort'
import BadgedShort from 'shared-components/ui/BadgedShort'
import IconedListItem from 'shared-components/ui/IconedListItem'
import ProductFeatures from 'shared-components/ui/ProductFeatures'
import ReviewHeroLow from 'shared-components/ui/ReviewHeroLow'
import ReviewScores from 'shared-components/ui/ReviewScores'
import ScoreRing from 'shared-components/ui/ScoreRing'
import SplitterLine from 'shared-components/ui/SplitterLine'
import Check from 'shared-svg/Check.svg'
import Close from 'shared-svg/CloseBold.svg'
import { DSReviewProductPageSG } from 'types.sg.ds'
import styles from './LegacyReviewHeader.module.css'

interface LegacyReviewHeaderProps {
  page: DSReviewProductPageSG
}

const LegacyReviewHeader: React.FC<LegacyReviewHeaderProps & React.PropsWithChildren> = ({
  page,
  children,
}) => {
  const { enqueue } = NotificationsContext.useContainer()
  const {
    common: { strings },
  } = CommonDataContext.useContainer()
  return (
    <>
      <ReviewHeroLow post={page}>
        {children}
        <SplitterLine className={clsx(styleUtils.mobileFull, styles.heroSplitter)} />
      </ReviewHeroLow>
      <div className={styles.infoGrid}>
        <BadgedProductShort className={styles.badgedProductShort} review={page} />
        <BadgedShort
          className={styles.badgedShort}
          image={page.badges.length ? page.badges[0] : null}
          score={null}
          bottomLine={page.bottomLineContent.text}
          title={page.device.name}
        />
        <ProductFeatures className={styles.features} vars={page.features} />
        <div className={clsx(styles.evaluates, styles.evaluatesPositive)}>
          <div className={styles.h3Title}>{page.table.positive.title}</div>
          {page.table.positive.list.map((item, index) => (
            <IconedListItem icon={Check} key={index} iconClassName={styles.iconPositive}>
              {item}
            </IconedListItem>
          ))}
        </div>
        <div className={clsx(styles.evaluates, styles.evaluatesNegative)}>
          <div className={styles.h3Title}>{page.table.negative.title}</div>
          {page.table.negative.list.map((item, index) => (
            <IconedListItem icon={Close} key={index} iconClassName={styles.iconNegative}>
              {item}
            </IconedListItem>
          ))}
        </div>
        <div className={styles.ringScores}>
          {page.device.ourScore ? (
            <div className={styles.ringScore}>
              <ScoreRing score={page.device.ourScore} variant="base" className={styles.scoreRing} />
              {'SoundGuys Rating'}
            </div>
          ) : null}
          {page.device.userScore ? (
            <div className={styles.ringScore}>
              <ScoreRing
                score={page.device.userScore}
                variant="violet"
                className={styles.scoreRing}
              />
              {'User Rating'}
            </div>
          ) : null}
        </div>
        <ReviewScores
          key={page.meta.postId}
          className={styles.scores}
          scores={page.scores}
          mdaqs={page.mdaqs}
          onSumbitNewScore={useCallback(
            async newVal => {
              const dataSource = await getds()
              await dataSource.sendScore(newVal)
              enqueue({ msg: strings.msgSuccess, duration: 3000, variant: 'success' })
              return true
            },
            [enqueue, strings]
          )}
        />
        <SplitterLine className={styles.splitter} />
      </div>
    </>
  )
}

export default LegacyReviewHeader
